/**
 * Group Messages Component for Re:Nav-96
 *
 * Displays messages and documents for a selected group in the message boards.
 * Supports message viewing, posting, document sharing, and real-time updates.
 *
 * Features:
 * - Tabbed interface for Messages and Documents
 * - Message list with author info and timestamps
 * - Message posting form
 * - Document upload and download
 * - Infinite scroll/load more
 * - Graceful loading and error states
 *
 * @example
 * ```tsx
 * <GroupMessages
 *   groupId={selectedGroupId}
 *   groupName="General Discussion"
 * />
 * ```
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { messagesApi, documentsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { MessageComposer } from './MessageComposer';
import { DocumentList, type Document } from './DocumentList';
import { DocumentUploader } from './DocumentUploader';
import { GroupResourcesPanel } from './GroupResourcesPanel';
import { GroupTasksPanel } from './GroupTasksPanel';
import type { Message, Group } from '@/types';
import './GroupMessages.css';

/**
 * Tab type for the group content
 */
type TabType = 'messages' | 'documents' | 'resources' | 'tasks';

/**
 * Props for the GroupMessages component
 */
interface GroupMessagesProps {
  /** ID of the group to display messages for */
  groupId: string | null;
  /** Name of the group for display */
  group: Group | null;
  /** Optional CSS class name for container */
  className?: string;
  /** Called when the user wants to open the folders/groups picker. When
   *  provided, a Filter button is rendered in the group header. */
  onOpenFilter?: () => void;
  /** Hide the group title section (icon + name + description). The
   *  group name is now surfaced via the MemberHeader breadcrumb. */
  hideHeader?: boolean;
}

/**
 * Format relative time for message display
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: diffDays > 365 ? 'numeric' : undefined,
  });
}

/**
 * Get author initials for avatar
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Loading skeleton for messages
 */
function MessagesLoadingSkeleton() {
  return (
    <div className="messages-loading">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="message-skeleton">
          <div className="message-skeleton-avatar" />
          <div className="message-skeleton-content">
            <div className="message-skeleton-header" />
            <div className="message-skeleton-text" />
            <div className="message-skeleton-text short" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no messages are available
 */
function MessagesEmptyState({ groupName }: { groupName: string }) {
  return (
    <div className="messages-empty">
      <svg
        className="w-16 h-16 text-white/20 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <h3 className="text-white/60 text-lg font-medium mb-2">
        No messages yet
      </h3>
      <p className="text-white/40 text-sm text-center max-w-sm">
        Be the first to start a conversation in {groupName}!
      </p>
    </div>
  );
}

/**
 * No group selected state
 */
function NoGroupSelectedState({ onOpenFilter }: { onOpenFilter?: () => void }) {
  return (
    <div className="messages-no-group">
      <svg
        className="w-16 h-16 text-white/20 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        />
      </svg>
      <h3 className="text-white/60 text-lg font-medium mb-2">
        Select a group
      </h3>
      <p className="text-white/40 text-sm text-center max-w-sm mb-4">
        Choose a group from the folder list to view messages.
      </p>
      {onOpenFilter && (
        <button type="button" onClick={onOpenFilter} className="sidebar-toggle">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 12h12M10 20h4" />
          </svg>
          <span className="sidebar-toggle-text">Pick a group</span>
        </button>
      )}
    </div>
  );
}

/**
 * Stream container. Renders message rows in chronological order
 * (oldest at top, newest at bottom) and auto-scrolls to the bottom
 * only when the user is already near the bottom — preserves the
 * read position when scrolled up in history.
 */
function MessagesStream({
  groupName,
  messages,
  isLoading,
  error,
  currentUserId,
  nextCursor,
  isLoadingMore,
  onLoadMore,
}: {
  groupName: string;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  currentUserId?: string;
  nextCursor: string | undefined;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const lastIdRef = useRef<string | null>(null);

  // Track "is at bottom" using scroll proximity (within 80px).
  const isAtBottom = () => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Auto-scroll on first render and on new last message — only when
  // the user was already near the bottom.
  useEffect(() => {
    const last = messages.length > 0 ? messages[messages.length - 1]!.rowKey : null;
    if (last === lastIdRef.current) return;
    const wasAtBottom = isAtBottom() || lastIdRef.current === null;
    lastIdRef.current = last;
    if (wasAtBottom) {
      // Wait for layout, then scroll. behavior: 'smooth' for poll
      // updates, instant for the initial paint.
      const behavior: ScrollBehavior = lastIdRef.current === last ? 'smooth' : 'auto';
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior, block: 'end' });
      });
    }
  }, [messages]);

  // Compute showAvatar/showAuthor: hide for consecutive messages from
  // the same author within a short window (< 5min).
  const decorated = useMemo(() => {
    const rows: Array<{ msg: Message; showAvatar: boolean; showAuthor: boolean }> = [];
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i]!;
      const prev = messages[i - 1];
      const sameAuthor = !!prev && prev.authorId === m.authorId;
      const closeInTime =
        !!prev && Math.abs(new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime()) < 5 * 60 * 1000;
      const grouped = sameAuthor && closeInTime;
      rows.push({ msg: m, showAvatar: !grouped, showAuthor: !grouped });
    }
    return rows;
  }, [messages]);

  return (
    <div
      ref={containerRef}
      className="messages-list flex-1 overflow-y-auto py-2"
    >
      {isLoading && messages.length === 0 ? (
        <MessagesLoadingSkeleton />
      ) : error ? (
        <div className="messages-error">
          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      ) : messages.length === 0 ? (
        <MessagesEmptyState groupName={groupName} />
      ) : (
        <>
          {nextCursor && (
            <div className="px-3 py-1 text-center">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="text-xs text-white/60 hover:text-white px-3 py-1 rounded border border-white/10 hover:bg-white/5"
              >
                {isLoadingMore ? 'Loading…' : 'Load older messages'}
              </button>
            </div>
          )}
          {decorated.map(({ msg, showAvatar, showAuthor }) => (
            <MessageItem
              key={msg.rowKey}
              message={msg}
              isCurrentUser={msg.authorId === currentUserId}
              showAvatar={showAvatar}
              showAuthor={showAuthor}
            />
          ))}
          <div ref={bottomRef} />
        </>
      )}
    </div>
  );
}

/**
 * Merge an incoming batch of messages into the current list. Returns
 * the *same array reference* when nothing changed so React skips
 * re-rendering. Otherwise returns a new sorted (oldest → newest) array
 * with stable references for unchanged items.
 */
function mergeMessages(prev: Message[], incoming: Message[]): Message[] {
  if (incoming.length === 0 && prev.length === 0) return prev;
  const byKey = new Map<string, Message>();
  for (const m of prev) byKey.set(m.rowKey, m);
  let changed = false;
  for (const m of incoming) {
    const existing = byKey.get(m.rowKey);
    if (!existing) {
      byKey.set(m.rowKey, m);
      changed = true;
    } else if (
      existing.content !== m.content ||
      existing.status !== m.status ||
      existing.authorName !== m.authorName
    ) {
      byKey.set(m.rowKey, m);
      changed = true;
    }
  }
  if (!changed) return prev;
  return Array.from(byKey.values()).sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * Slim WhatsApp-style message row.
 * - Own messages are right-aligned with a primary-tinted bubble.
 * - Others left-aligned with a neutral bubble.
 * - Consecutive messages from the same author share an avatar column
 *   and omit the author label (showAvatar / showAuthor props).
 */
interface MessageItemProps {
  message: Message;
  isCurrentUser: boolean;
  showAvatar: boolean;
  showAuthor: boolean;
}

const MessageItem = memo(function MessageItem({
  message,
  isCurrentUser,
  showAvatar,
  showAuthor,
}: MessageItemProps) {
  const initials = getInitials(message.authorName);
  const bubbleClasses = isCurrentUser
    ? 'bg-primary/20 text-white border border-primary/30 rounded-2xl rounded-br-md'
    : 'bg-white/[0.04] text-white border border-white/10 rounded-2xl rounded-bl-md';
  return (
    <div className={`flex items-end gap-2 px-3 ${isCurrentUser ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
      {!isCurrentUser && (
        <div className="w-7 h-7 flex-shrink-0">
          {showAvatar ? (
            message.authorAvatarUrl ? (
              <img src={message.authorAvatarUrl} alt={message.authorName} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <span className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-[10px] text-primary font-medium">
                {initials}
              </span>
            )
          ) : null}
        </div>
      )}
      <div className={`max-w-[78%] ${bubbleClasses} px-3 py-1.5`}>
        {showAuthor && !isCurrentUser && (
          <p className="text-[11px] font-medium text-primary mb-0.5">{message.authorName}</p>
        )}
        <p className="text-sm whitespace-pre-wrap break-words leading-snug">{message.content}</p>
        <p className={`text-[10px] text-white/40 mt-0.5 ${isCurrentUser ? 'text-right' : 'text-left'}`}>
          {formatRelativeTime(message.createdAt)}
          {message.status === 'pending' && (
            <span className="ml-1.5 text-yellow-400">• pending</span>
          )}
        </p>
      </div>
      {isCurrentUser && (
        <div className="w-7 h-7 flex-shrink-0">
          {showAvatar ? (
            message.authorAvatarUrl ? (
              <img src={message.authorAvatarUrl} alt={message.authorName} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <span className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-[10px] text-primary font-medium">
                {initials}
              </span>
            )
          ) : null}
        </div>
      )}
    </div>
  );
});

/**
 * GroupMessages Component
 *
 * Displays messages and documents for a selected group.
 * Supports message viewing, posting, and document sharing.
 *
 * @param props - Component props
 * @returns The group messages component
 *
 * @example
 * // Basic usage
 * <GroupMessages
 *   groupId={selectedGroupId}
 *   group={selectedGroup}
 * />
 */
export function GroupMessages({
  groupId,
  group,
  className = '',
  onOpenFilter,
  hideHeader = false,
}: GroupMessagesProps) {
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('messages');

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  // Fetch messages when group changes, then poll for updates.
  // On poll, merge incoming messages into the existing list by rowKey
  // so unchanged items keep their React identity (no flicker / repaint
  // of the whole list).
  useEffect(() => {
    if (!groupId) {
      setMessages([]);
      return;
    }
    let mounted = true;

    async function fetchMessages(isInitial: boolean) {
      try {
        if (isInitial) setIsLoadingMessages(true);
        setMessagesError(null);
        const data = await messagesApi.list(groupId as string, { limit: 50 });
        if (!mounted) return;
        if (isInitial) setNextCursor(data.nextCursor);
        setMessages((prev) => mergeMessages(prev, data.messages));
      } catch {
        if (mounted && isInitial) setMessagesError('Failed to load messages');
      } finally {
        if (mounted && isInitial) setIsLoadingMessages(false);
      }
    }

    void fetchMessages(true);
    // Poll every 10s — backend returns latest first; merge is cheap.
    const intervalId = setInterval(() => void fetchMessages(false), 10000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [groupId]);

  // Fetch documents when group changes or documents tab is active
  useEffect(() => {
    if (!groupId || activeTab !== 'documents') {
      return;
    }

    let mounted = true;

    async function fetchDocuments() {
      try {
        setIsLoadingDocuments(true);
        setDocumentsError(null);
        const data = await documentsApi.list(groupId as string);
        if (mounted) {
          setDocuments(data.data);
        }
      } catch (err) {
        if (mounted) {
          setDocumentsError('Failed to load documents');
        }
      } finally {
        if (mounted) {
          setIsLoadingDocuments(false);
        }
      }
    }

    fetchDocuments();

    return () => {
      mounted = false;
    };
  }, [groupId, activeTab]);

  // Reset tab when group changes
  useEffect(() => {
    setActiveTab('messages');
  }, [groupId]);

  // Load older messages (prepended in chronological order).
  const loadMore = useCallback(async () => {
    if (!groupId || !nextCursor || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const data = await messagesApi.list(groupId, { limit: 50, cursor: nextCursor });
      setMessages((prev) => mergeMessages(prev, data.messages));
      setNextCursor(data.nextCursor);
    } catch {
      // Silently fail on load more
    } finally {
      setIsLoadingMore(false);
    }
  }, [groupId, nextCursor, isLoadingMore]);

  // Optimistic insert when the user posts a message.
  const handleMessageSent = useCallback((newMessage: Message) => {
    setMessages((prev) => mergeMessages(prev, [newMessage]));
  }, []);

  // Handle document uploaded
  const handleDocumentUploaded = useCallback((doc: { id: string; filename: string; blobUrl: string }) => {
    // Add the new document to the list
    const newDoc: Document = {
      id: doc.id,
      filename: doc.filename,
      uploaderId: user?.id || '',
      uploaderName: user?.displayName,
      createdAt: new Date().toISOString(),
    };
    setDocuments((prev) => [newDoc, ...prev]);
  }, [user]);

  // Handle document deleted
  const handleDocumentDeleted = useCallback((documentId: string) => {
    setDocuments((prev) => prev.filter((d) => d.id !== documentId));
  }, []);

  if (!groupId || !group) {
    return (
      <div className={`messages-container ${className}`}>
        <NoGroupSelectedState onOpenFilter={onOpenFilter} />
      </div>
    );
  }

  return (
    <div className={`messages-container ${className}`}>
      {/* Header — hidden when the parent surface (e.g. the new
          /m/groups/:groupId route) already shows the group name in
          the breadcrumb. */}
      {!hideHeader && (
        <div className="messages-header">
          <div className="messages-header-info">
            <div className="messages-header-icon">
              <svg
                className="w-5 h-5 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div>
              <h2 className="messages-header-title">{group.name}</h2>
              {group.description && (
                <p className="messages-header-description">{group.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {group.requireApproval && (
              <span className="messages-moderated-badge">Moderated</span>
            )}
            {onOpenFilter && (
              <button
                type="button"
                onClick={onOpenFilter}
                className="sidebar-toggle"
                aria-label="Filter folders and groups"
                title="Filter folders and groups"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 12h12M10 20h4" />
                </svg>
                <span className="sidebar-toggle-text">Filter</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="group-tabs">
        <button
          type="button"
          className={`group-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          Messages
          {messages.length > 0 && (
            <span className="group-tab-badge">{messages.length}</span>
          )}
        </button>
        <button
          type="button"
          className={`group-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          Documents
          {documents.length > 0 && (
            <span className="group-tab-badge">{documents.length}</span>
          )}
        </button>
        <button
          type="button"
          className={`group-tab ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          Resources
        </button>
        <button
          type="button"
          className={`group-tab ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          Tasks
        </button>
      </div>

      {/* Messages Tab Content */}
      {activeTab === 'messages' && (
        <>
          <MessagesStream
            groupName={group.name}
            messages={messages}
            isLoading={isLoadingMessages}
            error={messagesError}
            currentUserId={user?.id}
            nextCursor={nextCursor}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />

          {/* Compose form */}
          <div className="messages-compose">
            <MessageComposer
              groupId={groupId}
              allowMemberPosts={group.allowMemberPosts}
              onMessageSent={handleMessageSent}
              requireApproval={group.requireApproval}
            />
          </div>
        </>
      )}

      {/* Documents Tab Content */}
      {activeTab === 'documents' && (
        <div className="documents-tab-content">
          <div className="documents-upload-section">
            <DocumentUploader
              groupId={groupId}
              onUploadComplete={handleDocumentUploaded}
            />
          </div>
          <div className="documents-list-section">
            <DocumentList
              groupId={groupId}
              documents={documents}
              isLoading={isLoadingDocuments}
              error={documentsError}
              onDocumentDeleted={handleDocumentDeleted}
            />
          </div>
        </div>
      )}

      {/* Resources Tab Content */}
      {activeTab === 'resources' && (
        <div className="documents-tab-content">
          <GroupResourcesPanel groupId={groupId} />
        </div>
      )}

      {/* Tasks Tab Content */}
      {activeTab === 'tasks' && (
        <div className="documents-tab-content">
          <GroupTasksPanel groupId={groupId} />
        </div>
      )}
    </div>
  );
}

export default GroupMessages;
